import { gql } from '@apollo/client'
import moment from 'moment-timezone'

import { CONFIG_OPTION_EMPTY } from '~/lib/partsConstants'
import LocalStorageWrapper from '~/lib/LocalStorageWrapper'

export const createOpenInviteOnQuoteMutation = gql`
    mutation createOpenInviteOnQuote($id: UUID!, $senderId: Int) {
        createOpenInviteOnQuote(quoteId: $id, senderId: $senderId) {
            id
            openEndedInviteUrl(senderId: $senderId)
        }
    }
`

export const quoteRequestChangeMutation = gql`
    mutation SubmitQuoteChangeRequest(
        $submitQuoteChangeRequestId: UUID!
        $changeRequestInput: ChangeRequestInput!
    ) {
        submitQuoteChangeRequest(
            id: $submitQuoteChangeRequestId
            ChangeRequestInput: $changeRequestInput
        ) {
            id
            isLocked
        }
    }
`

export const PROVISIONED_UI_FLAGS = Object.freeze({
    DISABLE_3DP_MATERIALS_COV19: 'DISABLE_3DP_MATERIALS_COV19',
    DISABLE_DRIFT_MESSAGING: 'DISABLE_DRIFT_MESSAGING',
    DISABLE_LEAD_TIMES_COV19: 'DISABLE_LEAD_TIMES_COV19',
    DISPLAY_EXPORT_CONTROL_SELF_CERTIFICATION:
        'DISPLAY_EXPORT_CONTROL_SELF_CERTIFICATION',
    ENABLE_FPQ_FE: 'ENABLE_FPQ_FE',
    FLAG_REORDER: 'FLAG_REORDER',
    HIDE_3DP_OPTION: 'HIDE_3DP_OPTION',
    HIDE_DIECAST_OPTION: 'HIDE_DIECAST_OPTION',
    HIDE_IM_OPTION: 'HIDE_IM_OPTION',
    HIDE_INTERNATIONAL_ORDERING: 'HIDE_INTERNATIONAL_ORDERING',
    HIDE_PO_ONBOARD: 'HIDE_PO_ONBOARD',
    HIDE_RTV_OPTION: 'HIDE_RTV_OPTION',
    IM_SHIPMENTS_UI: 'IM_SHIPMENTS_UI',
    NOTIFY_INCOTERMS_CHANGE: 'NOTIFY_INCOTERMS_CHANGE',
    PARTIALLY_SUPPORTED_CNC_MATERIALS: 'PARTIALLY_SUPPORTED_CNC_MATERIALS',
    RELEASE_ADVANCED_INSPECTION: 'RELEASE_ADVANCED_INSPECTION',
    RELEASE_CNC_PREMIUM_LEAD_TIME: 'RELEASE_CNC_PREMIUM_LEAD_TIME',
    RELEASE_IM_UI: 'RELEASE_IM_UI',
    RELEASE_PRE_CHECKOUT_QUOTE_PDF: 'RELEASE_PRE_CHECKOUT_QUOTE_PDF',
    RESTRICT_PROCESS_TO_CNC: 'RESTRICT_PROCESS_TO_CNC',
    IM_TOOLING_UI: 'IM_TOOLING_UI',
    SHOW_CORONAVIRUS_ALERT: 'SHOW_CORONAVIRUS_ALERT',
    SHOW_LEADTIME_EXT_MSG_COV19: 'SHOW_LEADTIME_EXT_MSG_COV19',
    USE_MATERIAL_CONFIG_QUERY: 'USE_MATERIAL_CONFIG_QUERY',
    DFM_LAYERS_DISMISS_CONFIGURABLE_REQUIREMENTS:
        'DFM_LAYERS_DISMISS_CONFIGURABLE_REQUIREMENTS',
    PDFOCR_AUTO_SANITIZATION: 'PDFOCR_AUTO_SANITIZATION',
    QUOTE_DETAILS_QUERY_REFACTOR: 'QUOTE_DETAILS_QUERY_REFACTOR',
    RELEASE_GET_EARLY_ACCESS_TEAMS: 'RELEASE_GET_EARLY_ACCESS_TEAMS',
    RELEASE_FORWARD_TO_PURCHASER: 'RELEASE_FORWARD_TO_PURCHASER',
    TRUST_IN_ANNOTATIONS: 'TRUST_IN_ANNOTATIONS',
    QLT_OPTIMIZATION: 'QLT_OPTIMIZATION',
    QLT_OPTIMIZATION_ASSISTANCE: 'QLT_OPTIMIZATION_ASSISTANCE',
    IM_PART_MOLD_ESTIMATION: 'IM_PART_MOLD_ESTIMATION',
    RELEASE_BA_ROLES_FE: 'RELEASE_BA_ROLES_FE',
    RELEASE_PREMIUM_UPSELL_DRAWER_UI: 'RELEASE_PREMIUM_UPSELL_DRAWER_UI',
    IM_MOLD_REPAIR_UI: 'IM_MOLD_REPAIR_UI',
    PART_REFACTOR_PHASE_2_UI: 'PART_REFACTOR_PHASE_2_UI',
    CNC_GLOBALIZATION_UI_V2: 'CNC_GLOBALIZATION_UI_V2',
    IM_SHOW_ESTIMATED_DELIVERY_DATE: 'IM_SHOW_ESTIMATED_DELIVERY_DATE',
    UI_SEND_RFQ_NOTES: 'UI_SEND_RFQ_NOTES',
    BUILDER_MIN_MARKUP_PCT: 'BUILDER_MIN_MARKUP_PCT',
    RELEASE_FINANCE_ROLES_VISIBILITY: 'RELEASE_FINANCE_ROLES_VISIBILITY',
    UI_CPQ_QUOTES_INTEGRATION_V1: 'UI_CPQ_QUOTES_INTEGRATION_V1',
    UI_CPQ_QUOTES_INTEGRATION_V2: 'UI_CPQ_QUOTES_INTEGRATION_V2',
    ENABLE_PART_AUTOCONFIG_UI: 'ENABLE_PART_AUTOCONFIG_UI',
    RELEASE_PRICE_MATCH: 'RELEASE_PRICE_MATCH',
    NON_IM_QUOTE_LEVEL_ATTACHMENT_UI: 'NON_IM_QUOTE_LEVEL_ATTACHMENT_UI',
    ENABLE_PUNCHOUT_UI: 'ENABLE_PUNCHOUT_UI',
    ENABLE_PUNCHOUT_PREVIEW_UI: 'ENABLE_PUNCHOUT_PREVIEW_UI',
    NON_IM_DFM_UI: 'NON_IM_DFM_UI',
    ENABLE_JOB_BOARD_MULTI_REGION_FE: 'ENABLE_JOB_BOARD_MULTI_REGION_FE',
    ENABLE_IM_UPSELL: 'ENABLE_IM_UPSELL',
    ENABLE_ALL_DAY_JOB_BOARD_UI: 'ENABLE_ALL_DAY_JOB_BOARD_UI',
    ENABLE_CONFIG_MULTIPLE_CTA_UI: 'ENABLE_CONFIG_MULTIPLE_CTA_UI',
    ADD_PURCHASABLE_STATE: 'ADD_PURCHASABLE_STATE_FE',
    HT_SHIPPING_ADDRESS_SYNC_UI: 'HT_SHIPPING_ADDRESS_SYNC_UI',
    ENABLE_DELETED_QUOTE: 'ENABLE_DELETED_QUOTE',
    NON_CORE_PROCESSES_SM_UI: 'NON_CORE_PROCESSES_SM_UI',
    NON_CORE_PROCESSES_CM_UI: 'NON_CORE_PROCESSES_CM_UI',
    NON_CORE_PROCESSES_DC_UI: 'NON_CORE_PROCESSES_DC_UI',
    ENABLE_MATERIAL_AI: 'ENABLE_MATERIAL_AI',
} as const)

const storage = new LocalStorageWrapper()

export const getLocalStorageItem = (key: string) => storage.getItem(key)

export const setLocalStorageItem = (
    key: string,
    value: string | boolean | number,
) => storage.setItem(key, value)

export const DONT_SHOW_ANNOTATION_INTRODUCTION =
    'DONT_SHOW_ANNOTATION_INTRODUCTION'

export const DONT_SHOW_COST_AND_LEADTIME_MODAL =
    'DONT_SHOW_COST_AND_LEADTIME_MODAL'

export function formatOrderPlacedDate(date: string | Date) {
    return date
        ? moment(date).format('dddd MMMM DD, YYYY')
        : CONFIG_OPTION_EMPTY
}
